'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

interface SeverityExplainerProps {
  score: number;
  severity: string;
}

interface SeverityInfo {
  level: string;
  range: string;
  description: string;
  action: string;
  color: string;
}

const severityData: Record<string, SeverityInfo> = {
  CRITICAL: {
    level: 'Critical',
    range: '9.0 - 10.0',
    description: 'This vulnerability poses an immediate, severe risk. Exploitation is typically straightforward and can result in complete system compromise.',
    action: 'Patch immediately or implement emergency mitigations.',
    color: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  },
  HIGH: {
    level: 'High',
    range: '7.0 - 8.9',
    description: 'This vulnerability presents significant risk. While exploitation may require specific conditions, successful attacks can cause substantial damage.',
    action: 'Prioritize patching within your next maintenance window.',
    color: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
  },
  MEDIUM: {
    level: 'Medium',
    range: '4.0 - 6.9',
    description: 'This vulnerability has moderate impact. Exploitation typically requires additional factors or limited access.',
    action: 'Address during regular patch cycles.',
    color: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  },
  LOW: {
    level: 'Low',
    range: '0.1 - 3.9',
    description: 'This vulnerability has limited impact or is difficult to exploit.',
    action: 'Address as part of routine maintenance when resources permit.',
    color: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  },
  NONE: {
    level: 'None',
    range: '0.0',
    description: 'This CVE has been analyzed and found to have no security impact in this context.',
    action: 'No immediate action required.',
    color: 'border-gray-400 bg-gray-50 dark:bg-gray-800/50',
  },
};

export default function SeverityExplainer({ score, severity }: SeverityExplainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const severityKey = severity?.toUpperCase() || 'NONE';
  const info = severityData[severityKey] || severityData.NONE;

  return (
    <div className={`border-l-4 ${info.color} rounded-r-lg overflow-hidden`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/50 dark:hover:bg-gray-800/30 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
            What does {info.level} severity mean?
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {info.description}
            </p>
          </div>

          <div className="flex items-start gap-2 p-3 bg-white/60 dark:bg-gray-800/40 rounded-lg">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase whitespace-nowrap">
              Recommended Action:
            </span>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {info.action}
            </p>
          </div>

          <div className="text-xs text-gray-700 dark:text-gray-300">
            This CVE scores <span className="font-semibold">{score.toFixed(1)}</span> (Range: {info.range})
          </div>
        </div>
      )}
    </div>
  );
}
